import ExcelJS from 'exceljs';

/** Keep in sync with backend `MAX_IMPORT_ROWS` in timeEntryImportService.ts */
export const MAX_IMPORT_ROWS = 500;

export interface ImportTemplateData {
  customers: Array<{ id: string; accountName: string }>;
  engagementRows: Array<{
    customerName: string;
    engagementDisplayName: string;
    engagementId: string;
  }>;
  groupRows: Array<{
    engagementDisplayName: string;
    groupName: string;
  }>;
  activityTypeRows: Array<{
    engagementDisplayName: string;
    activityTypeLabel: string;
  }>;
  timeSpentOptions: number[];
}

const HEADER_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FF4472C4' },
};

const HEADER_FONT: Partial<ExcelJS.Font> = {
  bold: true,
  color: { argb: 'FFFFFFFF' },
};

const LISTS_SHEET = 'Lists';
/** Text format so Excel does not reinterpret typed dates using the system locale (e.g. MM/DD). */
const WORKED_DATE_NUMFMT = '@';
const WORKED_DATE_HEADER = 'Worked Date (DD/MM/YYYY)*';

const MANDATORY_HEADERS = [
  WORKED_DATE_HEADER,
  'Customer*',
  'Engagement*',
  'Group*',
  'Type*',
  'Time Spent (In Mins)*',
  'Task',
  'Description*',
];

type WorksheetWithDataValidations = ExcelJS.Worksheet & {
  dataValidations: {
    add: (
      range: string,
      rule: {
        type: string;
        operator?: string;
        allowBlank?: boolean;
        formulae?: Array<string | Date | number>;
        showInputMessage?: boolean;
        promptTitle?: string;
        prompt?: string;
        showErrorMessage?: boolean;
        errorTitle?: string;
        error?: string;
      }
    ) => void;
  };
};

function asValidationWorksheet(worksheet: ExcelJS.Worksheet): WorksheetWithDataValidations {
  return worksheet as WorksheetWithDataValidations;
}

function writeColumnValues(
  sheet: ExcelJS.Worksheet,
  column: string,
  startRow: number,
  values: string[]
): { start: number; end: number } {
  values.forEach((value, index) => {
    sheet.getCell(`${column}${startRow + index}`).value = value;
  });
  if (values.length === 0) {
    sheet.getCell(`${column}${startRow}`).value = '';
    return { start: startRow, end: startRow };
  }
  return { start: startRow, end: startRow + values.length - 1 };
}

function addStaticListValidation(
  worksheet: ExcelJS.Worksheet,
  targetRange: string,
  listFormula: string,
  allowBlank: boolean,
  errorMessage: string
): void {
  asValidationWorksheet(worksheet).dataValidations.add(targetRange, {
    type: 'list',
    allowBlank,
    formulae: [listFormula],
    showErrorMessage: true,
    errorTitle: 'Invalid value',
    error: errorMessage,
  });
}

function addDependentListValidation(
  worksheet: ExcelJS.Worksheet,
  targetRange: string,
  offsetFormula: string,
  allowBlank: boolean,
  errorMessage: string
): void {
  asValidationWorksheet(worksheet).dataValidations.add(targetRange, {
    type: 'list',
    allowBlank,
    formulae: [offsetFormula],
    showErrorMessage: true,
    errorTitle: 'Invalid value',
    error: errorMessage,
  });
}

function buildWorkedDateValidationFormula(firstDataRow: number): string {
  const cell = `A${firstDataRow}`;
  const parsedDate = `DATE(VALUE(RIGHT(${cell},4)),VALUE(MID(${cell},4,2)),VALUE(LEFT(${cell},2)))`;
  return [
    `=AND(LEN(${cell})=10,`,
    `MID(${cell},3,1)="/",MID(${cell},6,1)="/",`,
    `ISNUMBER(${parsedDate}),`,
    `${parsedDate}>=DATE(2020,1,1),`,
    `${parsedDate}<=TODAY())`,
  ].join('');
}

function addDateValidation(
  worksheet: ExcelJS.Worksheet,
  targetRange: string,
  firstDataRow: number
): void {
  // Text-only DD/MM/YYYY — avoids Excel auto-parsing ambiguous dates as MM/DD.
  asValidationWorksheet(worksheet).dataValidations.add(targetRange, {
    type: 'custom',
    allowBlank: false,
    formulae: [buildWorkedDateValidationFormula(firstDataRow)],
    showInputMessage: true,
    promptTitle: 'Worked Date',
    prompt: 'Type DD/MM/YYYY (e.g. 04/09/2026 for 4 September 2026). Use slashes and leading zeros.',
    showErrorMessage: true,
    errorTitle: 'Invalid Worked Date',
    error: 'Enter a valid date in DD/MM/YYYY format that is not in the future.',
  });
}

function applyWorkedDateColumnFormat(
  worksheet: ExcelJS.Worksheet,
  firstDataRow: number,
  lastDataRow: number
): void {
  worksheet.getColumn(1).numFmt = WORKED_DATE_NUMFMT;
  for (let row = firstDataRow; row <= lastDataRow; row++) {
    worksheet.getCell(`A${row}`).numFmt = WORKED_DATE_NUMFMT;
  }
}

function sortEngagementRows(
  rows: ImportTemplateData['engagementRows']
): ImportTemplateData['engagementRows'] {
  return [...rows].sort((a, b) => {
    const customerCompare = a.customerName.localeCompare(b.customerName);
    if (customerCompare !== 0) return customerCompare;
    return a.engagementDisplayName.localeCompare(b.engagementDisplayName);
  });
}

export async function buildTimeEntryImportTemplate(
  data: ImportTemplateData
): Promise<Blob> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Pulse';
  workbook.created = new Date();

  const instructions = workbook.addWorksheet('Instructions');
  instructions.columns = [{ width: 110 }];
  instructions.addRow(['Record Time Import Template']);
  instructions.addRow([]);
  instructions.addRow(['Mandatory fields are marked with * in the Time Entries sheet.']);
  instructions.addRow(['Select values from dropdowns in this order: Customer → Engagement → Group / Type.']);
  instructions.addRow(['Worked Date: type DD/MM/YYYY as text (e.g. 04/09/2026 for 4 September 2026). Do not use the calendar picker.']);
  instructions.addRow(['Time Spent must be in 15-minute increments (15, 30, 45, 60, etc.).']);
  instructions.addRow(['Engagement options appear only after a Customer is selected.']);
  instructions.addRow(['Group and Type options appear only after an Engagement is selected.']);
  instructions.addRow([`Maximum ${MAX_IMPORT_ROWS} rows per import.`]);
  instructions.addRow([]);
  instructions.addRow(['Field reference (matches Record Time form):']);
  instructions.addRow(['  Worked Date* — Required. DD/MM/YYYY text (day/month/year), e.g. 04/09/2026.']);
  instructions.addRow(['  Customer* — Required. Select from dropdown.']);
  instructions.addRow(['  Engagement* — Required. Filtered by selected Customer.']);
  instructions.addRow(['  Group* — Required. Billing groups for the selected Engagement.']);
  instructions.addRow(['  Type* — Required. Filtered by selected Engagement.']);
  instructions.addRow(['  Time Spent (In Mins)* — Required. Minutes in 15-min increments.']);
  instructions.addRow(['  Task — Optional. Task or ticket reference.']);
  instructions.addRow(['  Description* — Required. Work description.']);

  const lists = workbook.addWorksheet(LISTS_SHEET);
  lists.state = 'veryHidden';

  lists.getCell('A1').value = 'Customer';
  lists.getCell('B1').value = 'Engagement';
  lists.getCell('D1').value = 'Engagement';
  lists.getCell('E1').value = 'Group';
  lists.getCell('G1').value = 'Engagement';
  lists.getCell('H1').value = 'Type';
  lists.getCell('J1').value = 'Customer List';
  lists.getCell('L1').value = 'Time Spent (In Mins)';

  const engagementRows = sortEngagementRows(data.engagementRows);
  const engagementLookupStart = 2;
  engagementRows.forEach((row, index) => {
    const rowNum = engagementLookupStart + index;
    lists.getCell(`A${rowNum}`).value = row.customerName;
    lists.getCell(`B${rowNum}`).value = row.engagementDisplayName;
  });
  const engagementLookupEnd = engagementRows.length > 0
    ? engagementLookupStart + engagementRows.length - 1
    : engagementLookupStart;

  const groupLookupStart = 2;
  data.groupRows.forEach((row, index) => {
    const rowNum = groupLookupStart + index;
    lists.getCell(`D${rowNum}`).value = row.engagementDisplayName;
    lists.getCell(`E${rowNum}`).value = row.groupName;
  });
  const groupLookupEnd = data.groupRows.length > 0
    ? groupLookupStart + data.groupRows.length - 1
    : groupLookupStart;

  const activityLookupStart = 2;
  data.activityTypeRows.forEach((row, index) => {
    const rowNum = activityLookupStart + index;
    lists.getCell(`G${rowNum}`).value = row.engagementDisplayName;
    lists.getCell(`H${rowNum}`).value = row.activityTypeLabel;
  });
  const activityLookupEnd = data.activityTypeRows.length > 0
    ? activityLookupStart + data.activityTypeRows.length - 1
    : activityLookupStart;

  const customerNames = data.customers.map((c) => c.accountName);
  const customerListRange = writeColumnValues(lists, 'J', 2, customerNames);
  const timeSpentRange = writeColumnValues(
    lists,
    'L',
    2,
    data.timeSpentOptions.map((m) => String(m))
  );

  const sheet = workbook.addWorksheet('Time Entries');
  sheet.views = [{ state: 'frozen', ySplit: 1 }];
  sheet.columns = [
    { width: 18 },
    { width: 28 },
    { width: 36 },
    { width: 28 },
    { width: 40 },
    { width: 20 },
    { width: 28 },
    { width: 50 },
  ];

  const headerRow = sheet.addRow(MANDATORY_HEADERS);
  headerRow.eachCell((cell) => {
    cell.fill = HEADER_FILL;
    cell.font = HEADER_FONT;
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
  });
  headerRow.height = 24;

  const firstDataRow = 2;
  const lastDataRow = firstDataRow + MAX_IMPORT_ROWS - 1;

  const customerListFormula =
    customerNames.length > 0
      ? `=${LISTS_SHEET}!$J$${customerListRange.start}:$J$${customerListRange.end}`
      : '=""';

  const engagementDependentFormula =
    engagementRows.length > 0
      ? `=OFFSET(${LISTS_SHEET}!$B$${engagementLookupStart},MATCH($B2,${LISTS_SHEET}!$A$${engagementLookupStart}:$A$${engagementLookupEnd},0)-1,0,COUNTIF(${LISTS_SHEET}!$A$${engagementLookupStart}:$A$${engagementLookupEnd},$B2),1)`
      : '=""';

  const groupDependentFormula =
    data.groupRows.length > 0
      ? `=OFFSET(${LISTS_SHEET}!$E$${groupLookupStart},MATCH($C2,${LISTS_SHEET}!$D$${groupLookupStart}:$D$${groupLookupEnd},0)-1,0,COUNTIF(${LISTS_SHEET}!$D$${groupLookupStart}:$D$${groupLookupEnd},$C2),1)`
      : '=""';

  const activityDependentFormula =
    data.activityTypeRows.length > 0
      ? `=OFFSET(${LISTS_SHEET}!$H$${activityLookupStart},MATCH($C2,${LISTS_SHEET}!$G$${activityLookupStart}:$G$${activityLookupEnd},0)-1,0,COUNTIF(${LISTS_SHEET}!$G$${activityLookupStart}:$G$${activityLookupEnd},$C2),1)`
      : '=""';

  const timeSpentFormula =
    data.timeSpentOptions.length > 0
      ? `=${LISTS_SHEET}!$L$${timeSpentRange.start}:$L$${timeSpentRange.end}`
      : '=""';

  addStaticListValidation(
    sheet,
    `B${firstDataRow}:B${lastDataRow}`,
    customerListFormula,
    false,
    'Please select a valid Customer.'
  );

  addDependentListValidation(
    sheet,
    `C${firstDataRow}:C${lastDataRow}`,
    engagementDependentFormula,
    true,
    'Please select an Engagement for the chosen Customer.'
  );

  addDependentListValidation(
    sheet,
    `D${firstDataRow}:D${lastDataRow}`,
    groupDependentFormula,
    true,
    'Please select a Group for the chosen Engagement.'
  );

  addDependentListValidation(
    sheet,
    `E${firstDataRow}:E${lastDataRow}`,
    activityDependentFormula,
    true,
    'Please select a Type for the chosen Engagement.'
  );

  addStaticListValidation(
    sheet,
    `F${firstDataRow}:F${lastDataRow}`,
    timeSpentFormula,
    false,
    'Please select time spent in 15-minute increments.'
  );

  applyWorkedDateColumnFormat(sheet, firstDataRow, lastDataRow);
  addDateValidation(sheet, `A${firstDataRow}:A${lastDataRow}`, firstDataRow);

  // Reference sheet for troubleshooting empty dependent lists (hidden).
  const reference = workbook.addWorksheet('Reference Data');
  reference.state = 'veryHidden';
  reference.addRow(['Customer', 'Engagement', 'Group', 'Type']);
  const maxRefRows = Math.max(
    engagementRows.length,
    data.groupRows.length,
    data.activityTypeRows.length
  );
  for (let i = 0; i < maxRefRows; i++) {
    reference.addRow([
      engagementRows[i]?.customerName ?? '',
      engagementRows[i]?.engagementDisplayName ?? '',
      data.groupRows[i]?.groupName ?? '',
      data.activityTypeRows[i]?.activityTypeLabel ?? '',
    ]);
  }

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

export function downloadTimeEntryImportTemplate(blob: Blob, filename?: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download =
    filename ||
    `record-time-import-template-${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const HEADER_ALIASES: Record<string, string> = {
  'worked date (dd/mm/yyyy)*': 'workedDate',
  'worked date (dd/mm/yyyy)': 'workedDate',
  'worked date*': 'workedDate',
  'worked date': 'workedDate',
  'date (dd/mm/yyyy)*': 'workedDate',
  'date (dd/mm/yyyy)': 'workedDate',
  date: 'workedDate',
  'customer*': 'customer',
  customer: 'customer',
  'engagement*': 'engagement',
  engagement: 'engagement',
  'group*': 'group',
  group: 'group',
  'type*': 'activityType',
  type: 'activityType',
  'activity type*': 'activityType',
  'activity type': 'activityType',
  activity: 'activityType',
  'time spent (in mins)*': 'timeSpent',
  'time spent (in mins)': 'timeSpent',
  'time spent': 'timeSpent',
  'time (minutes)': 'timeSpent',
  'time spent (minutes)*': 'timeSpent',
  'time spent (minutes)': 'timeSpent',
  task: 'task',
  'description*': 'description',
  description: 'description',
};

function normalizeHeader(header: string): string {
  return header.trim().toLowerCase().replace(/\s+/g, ' ');
}

function excelSerialToDmy(serial: number): string {
  const utcDays = Math.floor(serial - 25569);
  const date = new Date(utcDays * 86400 * 1000);
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();
  return `${day}/${month}/${year}`;
}

function dateToDmyUtc(date: Date): string {
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();
  return `${day}/${month}/${year}`;
}

function cellToString(value: unknown): string {
  if (value == null) return '';
  if (value instanceof Date) {
    return dateToDmyUtc(value);
  }
  if (typeof value === 'number' && Number.isFinite(value) && value > 20000 && value < 60000) {
    return excelSerialToDmy(value);
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return String(value).trim();
}

type XlsxCell = {
  t?: string;
  v?: unknown;
  w?: string;
};

function parseWorkedDateFromCell(cell: XlsxCell | undefined): string {
  if (!cell || cell.v == null || cell.v === '') return '';

  // Prefer stored text — template uses text format so DD/MM/YYYY is preserved.
  if (cell.t === 's' || typeof cell.v === 'string') {
    return String(cell.v).trim();
  }

  if (cell.t === 'd' && cell.v instanceof Date) {
    return dateToDmyUtc(cell.v);
  }

  if (typeof cell.v === 'number' && Number.isFinite(cell.v) && cell.v > 20000 && cell.v < 60000) {
    return excelSerialToDmy(cell.v);
  }

  if (cell.w) {
    return String(cell.w).trim();
  }

  return cellToString(cell.v);
}

function cellValueToString(cell: XlsxCell | undefined): string {
  if (!cell || cell.v == null || cell.v === '') return '';
  if (cell.w != null && cell.w !== '') {
    return String(cell.w).trim();
  }
  return cellToString(cell.v);
}

export interface ParsedImportRow {
  workedDate: string;
  customer: string;
  engagement: string;
  group: string;
  activityType: string;
  timeSpent: string;
  task: string;
  description: string;
}

export async function parseTimeEntryImportFile(file: File): Promise<ParsedImportRow[]> {
  const XLSX = await import('xlsx');
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: true, cellText: true });

  const sheetName =
    workbook.SheetNames.find((name) => name.toLowerCase() === 'time entries') ||
    workbook.SheetNames[0];
  const worksheet = workbook.Sheets[sheetName];
  if (!worksheet || !worksheet['!ref']) {
    throw new Error('No worksheet found in the uploaded file.');
  }

  const range = XLSX.utils.decode_range(worksheet['!ref']);
  const headerRowIndex = range.s.r;

  const columnFieldMap = new Map<number, string>();
  for (let col = range.s.c; col <= range.e.c; col++) {
    const headerCell = worksheet[XLSX.utils.encode_cell({ r: headerRowIndex, c: col })] as
      | XlsxCell
      | undefined;
    const headerText = cellValueToString(headerCell);
    const mapped = HEADER_ALIASES[normalizeHeader(headerText)];
    if (mapped) {
      columnFieldMap.set(col, mapped);
    }
  }

  const requiredFields = [
    'workedDate',
    'customer',
    'engagement',
    'group',
    'activityType',
    'timeSpent',
    'description',
  ];

  const missingHeaders = requiredFields.filter(
    (field) => !Array.from(columnFieldMap.values()).includes(field)
  );
  if (missingHeaders.length > 0) {
    throw new Error(
      'The uploaded file is missing required columns. Please download the latest template and try again.'
    );
  }

  const parsed: ParsedImportRow[] = [];

  for (let rowIndex = headerRowIndex + 1; rowIndex <= range.e.r; rowIndex++) {
    const row: Record<string, string> = {
      workedDate: '',
      customer: '',
      engagement: '',
      group: '',
      activityType: '',
      timeSpent: '',
      task: '',
      description: '',
    };

    for (const [col, field] of columnFieldMap.entries()) {
      const cell = worksheet[XLSX.utils.encode_cell({ r: rowIndex, c: col })] as
        | XlsxCell
        | undefined;
      row[field] =
        field === 'workedDate' ? parseWorkedDateFromCell(cell) : cellValueToString(cell);
    }

    const isEmpty = Object.values(row).every((v) => !v);
    if (isEmpty) continue;

    parsed.push({
      workedDate: row.workedDate,
      customer: row.customer,
      engagement: row.engagement,
      group: row.group,
      activityType: row.activityType,
      timeSpent: row.timeSpent,
      task: row.task,
      description: row.description,
    });
  }

  return parsed;
}

export function generateImportErrorsCsv(
  errors: Array<{ row: number; field: string; value?: string; message: string }>
): string {
  const headers = ['Row', 'Field', 'Value', 'Error'];
  const lines = [headers.join(',')];
  errors.forEach((error) => {
    const value = error.value ? `"${error.value.replace(/"/g, '""')}"` : '';
    const message = `"${error.message.replace(/"/g, '""')}"`;
    lines.push([error.row, `"${error.field}"`, value, message].join(','));
  });
  return lines.join('\n');
}

export function downloadTextFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
